import { clampRectToPage } from './elementGeometry';
import { CSS_PX_PER_MM } from './pageUnits';
import type { LayoutPictureSource, PageRect } from './layoutTypes';

export const MAX_PICTURE_BYTES = 8 * 1024 * 1024;

export const PICTURE_ACCEPT =
  '.bmp,.gif,.jpg,.jpeg,.png,.svg,.tif,.tiff,.webp,image/bmp,image/gif,image/jpeg,image/png,image/svg+xml,image/tiff,image/webp';

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  webp: 'image/webp',
};

export const PICTURE_MIME_TYPES = new Set(Object.values(MIME_BY_EXTENSION));

export function pictureMimeFromName(name: string, fallback = ''): string {
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXTENSION[extension] ?? fallback;
}

export function isSupportedPictureMime(mime: string, name = ''): boolean {
  const resolved = mime || pictureMimeFromName(name);
  return PICTURE_MIME_TYPES.has(resolved);
}

export function pictureNativeSizeMm(
  widthPx: number,
  heightPx: number,
): { readonly widthMm: number; readonly heightMm: number } {
  return {
    widthMm: Math.max(1, widthPx / CSS_PX_PER_MM),
    heightMm: Math.max(1, heightPx / CSS_PX_PER_MM),
  };
}

export function placePictureRect(
  pageWidthMm: number,
  pageHeightMm: number,
  widthMm: number,
  heightMm: number,
  origin: { readonly x: number; readonly y: number },
): PageRect {
  const maxWidth = Math.max(8, pageWidthMm - 8);
  const maxHeight = Math.max(8, pageHeightMm - 8);
  const scale = Math.min(1, maxWidth / widthMm, maxHeight / heightMm);
  return clampRectToPage(
    {
      x: origin.x,
      y: origin.y,
      width: Math.max(4, widthMm * scale),
      height: Math.max(4, heightMm * scale),
    },
    pageWidthMm,
    pageHeightMm,
  );
}

export function resizePictureKeepingAspect(
  current: PageRect,
  next: PageRect,
  naturalWidthPx: number,
  naturalHeightPx: number,
): PageRect {
  const aspect =
    naturalWidthPx > 0 && naturalHeightPx > 0
      ? naturalWidthPx / naturalHeightPx
      : current.width / Math.max(current.height, 1);
  const widthDriven = Math.abs(next.width - current.width) >= Math.abs(next.height - current.height);
  if (widthDriven) {
    const width = Math.max(4, next.width);
    return { ...next, width, height: width / aspect };
  }
  const height = Math.max(4, next.height);
  return { ...next, height, width: height * aspect };
}

export function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Unable to read picture data'));
    };
    reader.onerror = () => reject(new Error('Unable to read picture data'));
    reader.readAsDataURL(blob);
  });
}

export function measurePictureDataUrl(
  dataUrl: string,
): Promise<{ readonly width: number; readonly height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (width < 1 || height < 1) {
        reject(new Error('Picture has no size'));
        return;
      }
      resolve({ width, height });
    };
    image.onerror = () => reject(new Error('Unable to decode picture'));
    image.src = dataUrl;
  });
}

export async function layoutPictureSourceFromFile(file: File): Promise<LayoutPictureSource> {
  if (file.size > MAX_PICTURE_BYTES) {
    throw new Error('Picture is larger than 8 MB');
  }
  const mimeType = file.type || pictureMimeFromName(file.name);
  if (!isSupportedPictureMime(mimeType, file.name)) {
    throw new Error('Unsupported picture format');
  }
  const dataUrl = await readBlobAsDataUrl(file);
  const size = await measurePictureDataUrl(dataUrl);
  return {
    kind: 'file',
    name: file.name || 'Picture',
    href: null,
    dataUrl,
    mimeType,
    naturalWidthPx: size.width,
    naturalHeightPx: size.height,
  };
}

export async function layoutPictureSourceFromUrl(url: string): Promise<LayoutPictureSource> {
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('data:image/')) {
    throw new Error('Enter a http, https, or data image URL');
  }
  if (trimmed.startsWith('data:image/')) {
    const size = await measurePictureDataUrl(trimmed);
    return {
      kind: 'url',
      name: 'Picture',
      href: trimmed.slice(0, 96),
      dataUrl: trimmed,
      mimeType: trimmed.slice(5, trimmed.indexOf(';')) || 'image/png',
      naturalWidthPx: size.width,
      naturalHeightPx: size.height,
    };
  }
  const response = await fetch(trimmed);
  if (!response.ok) throw new Error(`Unable to fetch picture (${response.status})`);
  const blob = await response.blob();
  if (blob.size > MAX_PICTURE_BYTES) throw new Error('Picture is larger than 8 MB');
  const mimeType = blob.type || pictureMimeFromName(trimmed);
  if (!isSupportedPictureMime(mimeType, trimmed)) {
    throw new Error('Unsupported picture format');
  }
  const dataUrl = await readBlobAsDataUrl(blob);
  const size = await measurePictureDataUrl(dataUrl);
  const fileName = decodeURIComponent(trimmed.split('/').pop()?.split('?')[0] || 'Picture');
  return {
    kind: 'url',
    name: fileName || 'Picture',
    href: trimmed,
    dataUrl,
    mimeType,
    naturalWidthPx: size.width,
    naturalHeightPx: size.height,
  };
}
